import { DomainUpsert } from "@/components/domain";
import { SelectSearch } from "@/components/select-search";
import { DateKv } from "@/components/ui/date-kv";
import { Input } from "@/components/ui/input";
import { Kv } from "@/components/ui/kv";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useGetGuides } from "@/hooks/guides";
import { useGetRegions } from "@/hooks/regions";
import {
  CreateTour,
  defaultTourPercentagesValues,
  reservationRegionAsArray,
  tourCategoriesAsArray,
  tourTypesAsArray,
} from "@/models";
import { FormOptions } from "@deadcow-enterprises/react-forms";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export function Form({
  model,
  onUpsert,
}: {
  model?: CreateTour;
  onUpsert: (data: CreateTour) => Promise<void>;
}) {
  const { data: guides, isLoading: guidesLoading } = useGetGuides();
  const { data: regions, isLoading: regionsLoading } = useGetRegions();
  const { t } = useTranslation();
  const isEdit = useMemo(() => !!model, [model]);
  const formConfig = useMemo<FormOptions<CreateTour>>(() => {
    const config: FormOptions<CreateTour> = {
      name: {
        value: model?.name ?? "",
        validator: z.string().min(1, "errors.required"),
      },
      chainIndex: {
        value: model?.chainIndex,
      },
      active: {
        value: model?.active,
      },
      category: {
        value: model?.category,
      },
      exclusivity: {
        value: model?.exclusivity,
      },
      region: {
        value: model?.region,
      },
      type: {
        value: model?.type,
      },
      tourLeaderId: {
        value: model?.tourLeaderId,
      },
      percentageMap: {
        value: model?.percentageMap ?? defaultTourPercentagesValues,
      },
      details: {
        value: model?.details,
      },
      tourChainId: {
        value: model?.tourChainId,
      },
      fixedPrice: {
        value: model?.fixedPrice,
      },
      seasonalSupplementMap: {
        value: model?.seasonalSupplementMap,
      },
      individualRoomSupplementMap: {
        value: model?.individualRoomSupplementMap,
      },
      soloTravelerSupplement: {
        value: model?.soloTravelerSupplement,
      },
      operationRegion: {
        value: model?.operationRegion,
      },
    };
    return config;
  }, [model]);

  return (
    <DomainUpsert
      title={model?.name ?? t("tours.new.title")}
      buttonTranslation={isEdit ? "tours.update.button" : "tours.new.button"}
      formConfig={formConfig}
      onUpsert={onUpsert}
      fields={[
        {
          key: "name",
          label: "tours.fields.name",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.name}
              onChange={(ev) => {
                form.setValue("name", ev.target.value);
              }}
            />
          ),
        },
        {
          key: "category",
          label: "tours.fields.category",
          input: (form, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.category ?? undefined}
              onChange={(v) => {
                form.setValue("category", v);
              }}
              options={
                tourCategoriesAsArray?.map((v) => ({
                  value: v,
                  label: t(`tours.categories.${v}`),
                })) ?? []
              }
            />
          ),
        },
        {
          key: "region",
          label: "tours.fields.region",
          input: (form, id) =>
            regionsLoading || !regions ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <SelectSearch
                id={id}
                value={form.value.region?.toString() ?? undefined}
                onChange={(v) => {
                  form.setValue("region", parseInt(v));
                }}
                options={
                  regions.map((r) => ({
                    value: r.id,
                    label: r.name,
                  })) ?? []
                }
              />
            ),
        },
        {
          key: "operationRegion",
          label: "tours.fields.operationRegion",
          input: (form, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.operationRegion}
              onChange={(v) => {
                form.setValue("operationRegion", v);
              }}
              options={reservationRegionAsArray.map((or) => ({
                value: or,
                label: t(`common.operationRegions.${or}`),
              }))}
            />
          ),
        },
        {
          key: "type",
          label: "tours.fields.type",
          input: (form, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.type ?? undefined}
              onChange={(v) => {
                form.setValue("type", v);
              }}
              options={
                tourTypesAsArray?.map((v) => ({
                  value: v,
                  label: t(`tours.types.${v}`),
                })) ?? []
              }
            />
          ),
        },
        {
          key: "tourLeaderId",
          label: "tours.fields.tourLeaderId",
          input: (form, id) =>
            guidesLoading || !guides ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <SelectSearch
                id={id}
                value={form.value.tourLeaderId?.toString() ?? undefined}
                onChange={(v) => {
                  form.setValue("tourLeaderId", parseInt(v));
                }}
                options={
                  guides.map((o) => ({
                    value: o.id,
                    label: o.name,
                  })) ?? []
                }
              />
            ),
        },
        {
          key: "percentageMap",
          label: "tours.fields.percentageMap",
          input: (form, id) => (
            <Kv
              className="w-full"
              id={id}
              allowAdd={false}
              allowDelete={false}
              allowKeyEdit={false}
              onChange={(v) => {
                form.setValue("percentageMap", v);
              }}
              value={form.value.percentageMap ?? {}}
            />
          ),
        },
        {
          key: "details",
          label: "tours.fields.details",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.details ?? ""}
              onChange={(e) => {
                form.setValue("details", e.target.value);
              }}
            />
          ),
        },
        {
          key: "fixedPrice",
          label: "tours.fields.fixedPrice",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              step={0.01}
              min={0}
              inputMode="numeric"
              value={form.value.fixedPrice ?? ""}
              onChange={(e) => {
                form.setValue("fixedPrice", Number.parseFloat(e.target.value));
              }}
            />
          ),
        },
        {
          key: "seasonalSupplementMap",
          label: "tours.fields.seasonalSupplementMap",
          input: (form, id) => (
            <DateKv
              className="w-full"
              id={id}
              onChange={(v) => {
                form.setValue("seasonalSupplementMap", v);
              }}
              value={form.value.seasonalSupplementMap ?? {}}
            />
          ),
        },
        {
          key: "individualRoomSupplementMap",
          label: "tours.fields.individualRoomSupplementMap",
          input: (form, id) => (
            <DateKv
              className="w-full"
              id={id}
              onChange={(v) => {
                form.setValue("individualRoomSupplementMap", v);
              }}
              value={form.value.individualRoomSupplementMap ?? {}}
            />
          ),
        },
        {
          key: "soloTravelerSupplement",
          label: "tours.fields.soloTravelerSupplement",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              step={0.01}
              min={0}
              inputMode="numeric"
              value={form.value.soloTravelerSupplement ?? ""}
              onChange={(e) => {
                form.setValue(
                  "soloTravelerSupplement",
                  Number.parseFloat(e.target.value),
                );
              }}
            />
          ),
        },
        {
          key: "active",
          label: "tours.fields.active",
          orientation: "horizontal",
          input: (form, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("active", v);
              }}
              checked={!!form.value.active}
            />
          ),
        },
        {
          key: "exclusivity",
          label: "tours.fields.exclusivity",
          orientation: "horizontal",
          input: (form, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("exclusivity", v);
              }}
              checked={!!form.value.exclusivity}
            />
          ),
        },
      ]}
    />
  );
}
